import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ChevronRight, Mail, Users, Award, ArrowLeft, Loader, Building2, CreditCard } from 'lucide-react';
import { getImageUrl, fetchTendikByIdentifier } from '../utils/api';

const TENDIK_COLOR = 'from-[#7B2FBE] to-[#9333EA]'; // Ungu

const getInitials = (name) =>
  name?.replace(/^(Dr\.|apt\.|Ns\.|Drs\.)?\s*/, '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '??';

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
    <div className="w-8 h-8 rounded-lg bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center flex-shrink-0">
      <Icon size={15} className="text-teal-500" />
    </div>
    <div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-800 dark:text-white">{value}</p>
    </div>
  </div>
);

const TendikDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [tendik, setTendik] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Dukungan untuk ID angka (lama) atau slug (baru).
        const data = await fetchTendikByIdentifier(slug);
        if (data) setTendik(data);
        else setNotFound(true);
      } catch (err) {
        setNotFound(true);
      }
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-[70px]">
      <Loader className="animate-spin text-teal-500" size={40} />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center pt-[70px] text-gray-500">
      <p className="text-lg font-medium mb-4">Data tendik tidak ditemukan</p>
      <Link to="/dosen-tendik" className="text-teal-500 hover:text-teal-700 flex items-center gap-2">
        <ArrowLeft size={16} /> Kembali ke Dosen & Tendik
      </Link>
    </div>
  );

  const colorClass = TENDIK_COLOR;

  return (
    <div className="min-h-screen bg-[#F8F7FF] dark:bg-[#121020] pt-[70px]">
      {/* Hero */}
      <div className="page-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <div className="flex items-center gap-2 text-white/70 text-sm mb-6">
            <Link to="/" className="hover:text-white flex items-center gap-1">
              <Home size={14} /> Beranda
            </Link>
            <ChevronRight size={14} />
            <Link to="/dosen-tendik" className="hover:text-white">Dosen & Tendik</Link>
            <ChevronRight size={14} />
            <span className="text-white line-clamp-1">{tendik.nama}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-teal-500 hover:text-teal-700 mb-8 transition-colors"
        >
          <ArrowLeft size={16} /> Kembali
        </button>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">

          {/* Kolom kiri — foto & identitas */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="md:col-span-2"
          >
            <div className="glass-card rounded-2xl overflow-hidden">
              {/* Foto */}
              <div className={`py-6 bg-gradient-to-br ${colorClass} flex items-center justify-center`}>
                <div className="relative p-2 bg-white/20 backdrop-blur-md rounded-2xl shadow-xl">
                  {tendik.foto ? (
                    <img
                      src={getImageUrl(tendik.foto)}
                      alt={tendik.nama}
                      className="w-36 h-40 rounded-xl object-cover border-2 border-white/80 shadow-inner"
                    />
                  ) : (
                    <div className="w-36 h-40 rounded-xl bg-white/20 flex items-center justify-center text-white text-5xl font-bold border-2 border-white/80 shadow-inner">
                      {getInitials(tendik.nama)}
                    </div>
                  )}
                </div>
              </div>

              {/* Info dasar */}
              <div className="p-5 text-center">
                <span className="text-xs px-3 py-1 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 font-medium">
                  Tenaga Kependidikan
                </span>
                <h1 className="text-lg font-bold text-gray-800 dark:text-white mt-3 leading-tight">
                  {tendik.nama}
                </h1>
                <p className="text-teal-600 dark:text-teal-400 text-sm font-medium mt-1">
                  {tendik.jabatan || '-'}
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  {tendik.unit || '-'}
                </p>
                {tendik.email && (
                  <a
                    href={`mailto:${tendik.email}`}
                    title={tendik.email}
                    className="flex items-center justify-center gap-1.5 text-sm text-purple-500 hover:text-purple-700 mt-3 transition-colors max-w-full px-4"
                  >
                    <Mail size={14} className="flex-shrink-0" />
                    <span className="truncate">{tendik.email}</span>
                  </a>
                )}
              </div>
            </div>
          </motion.div>

          {/* Kolom kanan — detail */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="md:col-span-3 space-y-6"
          >
            {/* Identitas */}
            <div className="glass-card rounded-2xl p-6">
              <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                <Users size={18} className="text-teal-500" />
                Identitas
              </h2>
              <div className="space-y-0">
                {tendik.nip && <InfoRow icon={CreditCard} label="NIP" value={tendik.nip} />}
                {tendik.nitk && <InfoRow icon={CreditCard} label="NITK" value={tendik.nitk} />}
                <InfoRow icon={Award} label="Jabatan/Fungsi" value={tendik.jabatan || '-'} />
                <InfoRow icon={Building2} label="Unit" value={tendik.unit || '-'} />
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default TendikDetail;
